import { Field, inputClass } from '@/components/ui';
import { AU_STATES, type PostalAddress } from '@/lib/address';

/**
 * The address fields, one set for every screen that types an address: the
 * customer (spec 016 FR-1602) and the supplier (spec 013 FR-1303). The shape
 * and its checks are in `src/lib/address.ts`.
 *
 * `idPrefix` keeps two of these on one screen apart, for example the company
 * address and the delivery address.
 */
export function AddressFields({
  idPrefix,
  value,
  onChange,
  disabled,
}: {
  idPrefix: string;
  value: PostalAddress;
  onChange: (next: PostalAddress) => void;
  disabled?: boolean;
}) {
  const set = (k: keyof PostalAddress) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [k]: e.target.value });

  return (
    <div className="grid gap-3">
      <Field label="Street" htmlFor={`${idPrefix}-line1`}>
        <input id={`${idPrefix}-line1`} className={inputClass} value={value.line1}
          disabled={disabled} autoComplete="off" onChange={set('line1')} />
      </Field>
      <Field label="Street, second line" htmlFor={`${idPrefix}-line2`} optional>
        <input id={`${idPrefix}-line2`} className={inputClass} value={value.line2}
          disabled={disabled} autoComplete="off" onChange={set('line2')} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
        <Field label="Suburb" htmlFor={`${idPrefix}-suburb`}>
          <input id={`${idPrefix}-suburb`} className={inputClass} value={value.suburb}
            disabled={disabled} autoComplete="off" onChange={set('suburb')} />
        </Field>
        <Field label="State" htmlFor={`${idPrefix}-state`}>
          <input id={`${idPrefix}-state`} className={inputClass} value={value.state}
            list={`${idPrefix}-states`} disabled={disabled} autoComplete="off"
            onChange={set('state')} />
          <datalist id={`${idPrefix}-states`}>
            {AU_STATES.map((s) => <option key={s} value={s} />)}
          </datalist>
        </Field>
        <Field label="Postcode" htmlFor={`${idPrefix}-postcode`}>
          <input id={`${idPrefix}-postcode`} className={inputClass} value={value.postcode}
            inputMode="numeric" disabled={disabled} autoComplete="off" onChange={set('postcode')} />
        </Field>
      </div>
      <Field label="Country" htmlFor={`${idPrefix}-country`}>
        <input id={`${idPrefix}-country`} className={inputClass} value={value.country}
          disabled={disabled} autoComplete="off" onChange={set('country')} />
      </Field>
    </div>
  );
}
